/**
 * Rago-Proof v1 — device proof-of-possession header (plan §4).
 *
 * Header:  Rago-Proof: v1;did=<uuid>;alg=EdDSA|ES256;ts=<unix-s>;nonce=<b64u>;sig=<b64u>
 * Signed string (UTF-8):
 *   "v1\n{METHOD}\n{path+query}\n{ts}\n{nonce}\n{b64u(sha256(body || ""))}"
 *
 * This module is platform-neutral: canonicalization + header parse/format only.
 * Signing (WebCrypto) lives in the app; verification (node:crypto) in the API.
 */

export const PROOF_VERSION = "v1";
export const PROOF_HEADER = "rago-proof";
export const PROOF_TS_WINDOW_S = 120;

export type ProofAlg = "EdDSA" | "ES256";

export interface ProofParts {
  version: string;
  did: string;
  alg: ProofAlg;
  ts: number;
  nonce: string;
  sig: string;
}

/** atob/btoa exist in both browsers and Node ≥16, but not in the pure ES lib types. */
const g = globalThis as unknown as {
  btoa?: (s: string) => string;
  atob?: (s: string) => string;
  Buffer?: {
    from(data: Uint8Array | string, enc?: string): { toString(enc: string): string } & Uint8Array;
  };
};

export function b64uEncode(bytes: Uint8Array): string {
  let b64: string;
  if (g.btoa) {
    let bin = "";
    for (const b of bytes) bin += String.fromCharCode(b);
    b64 = g.btoa(bin);
  } else if (g.Buffer) {
    b64 = g.Buffer.from(bytes).toString("base64");
  } else {
    throw new Error("no base64 encoder available");
  }
  return b64.replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function b64uDecode(s: string): Uint8Array {
  const b64 = s.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat((4 - (s.length % 4)) % 4);
  if (g.atob) {
    const bin = g.atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  if (g.Buffer) return new Uint8Array(g.Buffer.from(b64, "base64"));
  throw new Error("no base64 decoder available");
}

/** The exact byte string a device signs. `bodySha256B64u` is b64u(sha256(raw body)) — empty body hashes "". */
export function canonicalProofString(input: {
  method: string;
  pathWithQuery: string;
  ts: number;
  nonce: string;
  bodySha256B64u: string;
}): string {
  return [
    PROOF_VERSION,
    input.method.toUpperCase(),
    input.pathWithQuery,
    String(input.ts),
    input.nonce,
    input.bodySha256B64u,
  ].join("\n");
}

export function formatProofHeader(p: Omit<ProofParts, "version">): string {
  return `${PROOF_VERSION};did=${p.did};alg=${p.alg};ts=${p.ts};nonce=${p.nonce};sig=${p.sig}`;
}

export function parseProofHeader(header: string): ProofParts | null {
  const segs = header.split(";");
  if (segs[0] !== PROOF_VERSION) return null;
  const kv = new Map<string, string>();
  for (const seg of segs.slice(1)) {
    const i = seg.indexOf("=");
    if (i <= 0) return null;
    kv.set(seg.slice(0, i), seg.slice(i + 1));
  }
  const did = kv.get("did");
  const alg = kv.get("alg");
  const ts = Number(kv.get("ts"));
  const nonce = kv.get("nonce");
  const sig = kv.get("sig");
  if (!did || !nonce || !sig || !Number.isFinite(ts)) return null;
  if (alg !== "EdDSA" && alg !== "ES256") return null;
  return { version: PROOF_VERSION, did, alg, ts, nonce, sig };
}
